import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Avatar,
  Snackbar,
} from '@mui/material';
import {
  SportsCricket,
  People,
  Public,
  Clear,
  DragIndicator,
  SwapHoriz,
  PersonAdd,
  Close,
  CheckCircle,
  Refresh,
} from '@mui/icons-material';
import { useLineupContext, Lineup, LineupPlayer } from '../contexts/LineupContext';
import { apiService } from '../services/api';

// Drag and Drop imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Player substitution interface
interface PlayerSubstitution {
  lineupId: number;
  playerToRemove: LineupPlayer;
  playerToAdd: LineupPlayer;
  position: number;
}

// Sortable Lineup Card Component
const SortableLineupCard: React.FC<{ 
  lineup: Lineup; 
  formatPlayerName: (player: LineupPlayer) => string; 
  getRoleColor: (role: string) => string;
  onPlayerSubstitution: (substitution: PlayerSubstitution) => void;
  isSubstituting: boolean;
  lastUpdatedLineupId: number | null;
}> = ({ 
  lineup, 
  formatPlayerName, 
  getRoleColor,
  onPlayerSubstitution,
  isSubstituting,
  lastUpdatedLineupId
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lineup.lineup_number });

  const [showSubstitutionDialog, setShowSubstitutionDialog] = useState(false);
  const [selectedPlayerToReplace, setSelectedPlayerToReplace] = useState<LineupPlayer | null>(null);
  const [remainingPlayers, setRemainingPlayers] = useState<LineupPlayer[]>([]);
  const [loadingRemainingPlayers, setLoadingRemainingPlayers] = useState<boolean>(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handlePlayerClick = async (player: LineupPlayer) => {
    console.log('Player clicked:', player.player_name);
    setSelectedPlayerToReplace(player);
    setLoadingRemainingPlayers(true);
    setShowSubstitutionDialog(true);
    
    try {
      console.log('Fetching remaining players...');
      // Call backend API to get remaining players
      const response = await apiService.getRemainingPlayers(3, lineup.starting_xi);
      console.log('Remaining players response:', response);
      setRemainingPlayers(response.players || []);
    } catch (error) {
      console.error('Error fetching remaining players:', error);
    } finally {
      setLoadingRemainingPlayers(false);
    }
  };

  const handleSubstitution = (newPlayer: LineupPlayer) => {
    if (selectedPlayerToReplace) {
      const position = lineup.starting_xi.findIndex(p => p.player_id === selectedPlayerToReplace.player_id);
      onPlayerSubstitution({
        lineupId: lineup.lineup_number,
        playerToRemove: selectedPlayerToReplace,
        playerToAdd: newPlayer,
        position: position
      });
      setShowSubstitutionDialog(false);
      setSelectedPlayerToReplace(null);
    }
  };

  // Check if this lineup was recently updated
  const isRecentlyUpdated = lastUpdatedLineupId === lineup.lineup_number;
  const isCurrentlySubstituting = isSubstituting && lastUpdatedLineupId === lineup.lineup_number;

  return (
    <>
      <Card 
        ref={setNodeRef} 
        style={style}
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          cursor: isDragging ? 'grabbing' : 'grab',
          border: isDragging ? '2px dashed #1976d2' : 
                  isRecentlyUpdated ? '2px solid #4caf50' : '1px solid #e0e0e0',
          boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,0.2)' : 
                      isRecentlyUpdated ? '0 4px 12px rgba(76,175,80,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
        }}
      >
        {/* Drag Handle */}
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            bgcolor: isRecentlyUpdated ? 'success.light' : 'grey.100',
            cursor: 'grab',
            '&:hover': {
              bgcolor: isRecentlyUpdated ? 'success.main' : 'grey.200',
            },
          }}
        >
          <DragIndicator color="action" />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Drag to reorder
          </Typography>
          {isRecentlyUpdated && (
            <CheckCircle color="success" sx={{ ml: 1, fontSize: 16 }} />
          )}
        </Box>

        {/* Lineup Header */}
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="primary">
              Lineup #{lineup.lineup_number}
              {isCurrentlySubstituting && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={`${lineup.indians} Indians`} 
                color="primary" 
                size="small" 
                icon={<People />}
              />
              <Chip 
                label={`${lineup.overseas} Overseas`} 
                color="secondary" 
                size="small" 
                icon={<Public />}
              />
            </Box>
          </Box>
          
          {/* Team Summary - Removed Squad Size */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Players: {lineup.total_players}
            </Typography>
          </Box>
        </CardContent>

        {/* Starting XI Table */}
        <CardContent sx={{ pt: 0, flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Starting XI
            </Typography>
            <Tooltip title="Click on any player to substitute">
              <Chip 
                icon={<SwapHoriz />}
                label="Click to substitute"
                size="small"
                color="info"
                variant="outlined"
              />
            </Tooltip>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Pos</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Player</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Bat TVAA</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Bowl TVAA</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineup.starting_xi.map((player, index) => (
                  <TableRow 
                    key={player.player_id} 
                    hover
                    onClick={() => handlePlayerClick(player)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'primary.light',
                        '& .MuiTableCell-root': {
                          color: 'white !important',
                        },
                        '& .MuiChip-root': {
                          color: 'white !important',
                          borderColor: 'white !important',
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'white !important',
                        },
                        '& .MuiTypography-root': {
                          color: 'white !important',
                        }
                      }
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {player.lineup_bat_pos === '-' ? '-' : player.lineup_bat_pos}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            fontSize: '0.75rem',
                            color: 'inherit',
                            fontWeight: 'inherit'
                          }}
                        >
                          {formatPlayerName(player)}
                        </Typography>
                        <PersonAdd fontSize="small" color="action" />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={player.role.replace('_', ' ')} 
                        color={getRoleColor(player.role) as any}
                        size="small"
                        sx={{ 
                          fontSize: '0.7rem', 
                          height: 20,
                          fontWeight: 'inherit'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {player.bat_tvaa_per_over?.toFixed(2) || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {player.bowl_tvaa_per_over?.toFixed(2) || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Substitute Details */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Substitutes
              </Typography>
              {isRecentlyUpdated && (
                <Chip 
                  icon={<Refresh />}
                  label="Updated"
                  size="small"
                  color="success"
                  sx={{ fontSize: '0.6rem', height: 18 }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={`Batter: ${lineup.substitute_details.batter_name}`}
                variant="outlined"
                size="small"
                sx={{ 
                  fontSize: '0.7rem',
                  borderColor: isRecentlyUpdated ? 'success.main' : undefined,
                  color: isRecentlyUpdated ? 'success.main' : undefined,
                }}
              />
              <Chip 
                label={`Bowler: ${lineup.substitute_details.bowler_name}`}
                variant="outlined"
                size="small"
                sx={{ 
                  fontSize: '0.7rem',
                  borderColor: isRecentlyUpdated ? 'success.main' : undefined,
                  color: isRecentlyUpdated ? 'success.main' : undefined,
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Player Substitution Dialog */}
      <Dialog 
        open={showSubstitutionDialog} 
        onClose={() => setShowSubstitutionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Substitute Player
            </Typography>
            <IconButton onClick={() => setShowSubstitutionDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPlayerToReplace && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Replacing: {formatPlayerName(selectedPlayerToReplace)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Position: {selectedPlayerToReplace.lineup_bat_pos === '-' ? 'Lower Order' : selectedPlayerToReplace.lineup_bat_pos}
              </Typography>
            </Box>
          )}
          
          <Typography variant="h6" gutterBottom>
            Available Players ({remainingPlayers.length})
          </Typography>
          
          {/* Progress Bar for Loading Remaining Players */}
          {loadingRemainingPlayers && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress color="info" />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Loading remaining players...
              </Typography>
            </Box>
          )}
          
          {!loadingRemainingPlayers && (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {remainingPlayers.map((player) => (
                <ListItem key={player.player_id} disablePadding>
                  <ListItemButton 
                    onClick={() => handleSubstitution(player)}
                    sx={{ 
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        bgcolor: 'primary.light',
                        color: 'white',
                      }
                    }}
                  >
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {player.nationality === 'India' ? '🇮🇳' : '🌍'}
                    </Avatar>
                    <ListItemText
                      primary={player.player_name}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="inherit">
                            Role: {player.role.replace('_', ' ')} | 
                            Bat TVAA: {player.bat_tvaa_per_over?.toFixed(2) || 'N/A'} | 
                            Bowl TVAA: {player.bowl_tvaa_per_over?.toFixed(2) || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="inherit">
                            Avg Position: {player.avg_bat_pos}
                          </Typography>
                        </Box>
                      }
                    />
                    <SwapHoriz color="action" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubstitutionDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const Lineups: React.FC = () => {
  const { lineupState, setLineups, setLoading, setError, clearLineups } = useLineupContext();
  const [nLineups, setNLineups] = useState<number>(3);
  
  // Local loading state for immediate UI feedback
  const [isLocalLoading, setIsLocalLoading] = useState<boolean>(false);
  
  // Substitution states
  const [isSubstituting, setIsSubstituting] = useState<boolean>(false);
  const [lastUpdatedLineupId, setLastUpdatedLineupId] = useState<number | null>(null);
  const [substitutionMessage, setSubstitutionMessage] = useState<string>('');

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Debug logging
  console.log('Lineups component render - lineupState:', lineupState);
  console.log('Local loading state:', isLocalLoading);

  // Track context loading state changes
  useEffect(() => {
    console.log('Context loading state changed to:', lineupState.isLoading);
  }, [lineupState.isLoading]);

  // Track local loading state changes
  useEffect(() => {
    console.log('Local loading state changed to:', isLocalLoading);
  }, [isLocalLoading]);

  // Clear highlight after 3 seconds
  useEffect(() => {
    if (lastUpdatedLineupId) {
      const timer = setTimeout(() => {
        setLastUpdatedLineupId(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [lastUpdatedLineupId]);

  const generateLineups = async () => {
    try {
      console.log('=== STARTING LINEUP GENERATION ===');
      
      // Set both local and context loading states
      setIsLocalLoading(true);
      setLoading(true);
      setError(null);
      
      console.log('Loading states set to true');
      
      // Add a minimum delay to ensure progress bar is visible
      const minDelay = new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Generating lineups...');
      const response = await apiService.generateLineups(3, nLineups);
      console.log('Response received:', response);
      
      // Wait for minimum delay
      await minDelay;
      console.log('Minimum delay completed');
      
      // Update lineups in context
      setLineups(response.lineups || []);
      console.log('Lineups set:', response.lineups?.length || 0);
      
      // Clear loading states
      setIsLocalLoading(false);
      setLoading(false);
      
      console.log('=== LINEUP GENERATION COMPLETED ===');
    } catch (error) {
      console.error('Error generating lineups:', error);
      setError('Failed to generate lineups. Please try again.');
      setIsLocalLoading(false);
      setLoading(false);
    }
  };

  // Handle drag end for reordering lineups
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = lineupState.lineups.findIndex(lineup => lineup.lineup_number === active.id);
      const newIndex = lineupState.lineups.findIndex(lineup => lineup.lineup_number === over.id);
      
      const newLineups = arrayMove(lineupState.lineups, oldIndex, newIndex);
      setLineups(newLineups);
    }
  };

  // Handle player substitution
  const handlePlayerSubstitution = async (substitution: PlayerSubstitution) => {
    const { lineupId, playerToRemove, playerToAdd, position } = substitution;
    
    console.log('Handling player substitution:', substitution);
    
    try {
      // Set substitution loading state
      setIsSubstituting(true);
      setLastUpdatedLineupId(lineupId);
      setSubstitutionMessage(`Substituting ${playerToRemove.player_name} with ${playerToAdd.player_name}...`);
      
      // Call backend API to substitute player and recalculate substitutes
      const response = await apiService.substitutePlayer(3, lineupId, playerToRemove, playerToAdd, position);
      
      if (response.success) {
        // Update the lineups with the response from backend
        setLineups(response.updatedLineups);
        setSubstitutionMessage(`Successfully substituted ${playerToAdd.player_name} and recalculated substitutes!`);
        console.log('Player substitution successful');
      } else {
        console.error('Player substitution failed:', response.error);
        setSubstitutionMessage('Substitution failed. Please try again.');
      }
    } catch (error) {
      console.error('Error substituting player:', error);
      setSubstitutionMessage('Error during substitution. Please try again.');
    } finally {
      setIsSubstituting(false);
    }
  };

  // Helper functions
  const formatPlayerName = (player: LineupPlayer) => {
    const flag = player.nationality === 'India' ? '🇮🇳' : '🌍';
    return `${flag} ${player.player_name}`;
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'top_order': return 'primary';
      case 'middle_lower_order': return 'secondary';
      case 'bowler': return 'error';
      default: return 'default';
    }
  };

  // Calculate overall loading state
  const isLoading = isLocalLoading || lineupState.isLoading;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SportsCricket color="primary" />
          Team Lineups
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate and compare optimized team lineups with drag-and-drop reordering and player substitution.
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <TextField
            label="Number of Lineups"
            type="number"
            value={nLineups}
            onChange={(e) => setNLineups(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 10 }}
            size="small"
            sx={{ width: 150 }}
          />
          <Button
            variant="contained"
            onClick={generateLineups}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <SportsCricket />}
            sx={{ minWidth: 200 }}
          >
            {isLoading ? 'Generating...' : 'Generate Lineups'}
          </Button>
          {lineupState.lineups.length > 0 && (
            <Button
              variant="outlined"
              onClick={clearLineups}
              startIcon={<Clear />}
              color="error"
            >
              Clear Lineups
            </Button>
          )}
        </Box>

        {/* Progress Bar */}
        {isLoading && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Generating optimized lineups...
            </Typography>
          </Box>
        )}

        {/* Substitution Progress */}
        {isSubstituting && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress color="secondary" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              {substitutionMessage}
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {lineupState.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {lineupState.error}
          </Alert>
        )}
      </Box>

      {/* Lineups Display */}
      {lineupState.lineups.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <People color="primary" />
            Generated Lineups ({lineupState.lineups.length})
            {lineupState.lastGenerated && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                Last generated: {lineupState.lastGenerated.toLocaleTimeString()}
              </Typography>
            )}
          </Typography>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={lineupState.lineups.map(lineup => lineup.lineup_number)}
              strategy={verticalListSortingStrategy}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                  gap: 3,
                }}
              >
                {lineupState.lineups.map((lineup) => (
                  <SortableLineupCard
                    key={lineup.lineup_number}
                    lineup={lineup}
                    formatPlayerName={formatPlayerName}
                    getRoleColor={getRoleColor}
                    onPlayerSubstitution={handlePlayerSubstitution}
                    isSubstituting={isSubstituting}
                    lastUpdatedLineupId={lastUpdatedLineupId}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {/* Empty State */}
      {lineupState.lineups.length === 0 && !isLoading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SportsCricket sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No lineups generated yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Generate Lineups" to create optimized team combinations
          </Typography>
        </Box>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!substitutionMessage && !isSubstituting}
        autoHideDuration={4000}
        onClose={() => setSubstitutionMessage('')}
        message={substitutionMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default Lineups;